import { colors } from 'Constants/Colors';
import { UserEvaluationI } from 'types';

export type EvaluationStatusKind = 'notTaken' | 'passed' | 'pending' | 'failed';

export interface EvaluationStatusDescriptor {
  /** Discriminated kind used to pick the i18n label. */
  kind: EvaluationStatusKind;
  /** Background color for the status Chip. */
  color: string;
  /** Grade to interpolate into the label (only relevant for passed/failed). */
  grade: number | null;
}

const PASSING_GRADE = 70;

/**
 * Pure resolver for the evaluation status shown inside the modal.
 *
 * Rules (mirrors EvaluationCard/useEvaluationStatus, decoupled on purpose):
 *  - userEvaluationId == null                       => "notTaken" (neutral)
 *  - grade == null                                  => "notTaken" (neutral)
 *  - grade >= 70                                    => "passed" (green)
 *  - grade < 70 && attemptsCount < maxAttempts      => "pending" (neutral)
 *  - grade < 70 && attemptsCount === maxAttempts    => "failed" (red)
 *
 * When attempt counters are unknown we fall back to "failed" so a sub-70
 * grade is never silently hidden.
 */
export function resolveEvaluationStatus(
  evaluation: Pick<UserEvaluationI, 'userEvaluationId' | 'grade' | 'attemptsCount' | 'maxAttempts'>
): EvaluationStatusDescriptor {
  const { userEvaluationId, grade, attemptsCount, maxAttempts } = evaluation;

  if (userEvaluationId == null || grade == null) {
    return { kind: 'notTaken', color: colors.GRAY, grade: null };
  }

  if (grade >= PASSING_GRADE) {
    return { kind: 'passed', color: colors.GREEN_LIGHT, grade };
  }

  const hasAttemptsRemaining =
    typeof attemptsCount === 'number' && typeof maxAttempts === 'number' && attemptsCount < maxAttempts;

  if (hasAttemptsRemaining) {
    return { kind: 'pending', color: colors.GRAY, grade };
  }

  return { kind: 'failed', color: colors.RED, grade };
}
